// The template barrel and the generationTool barrel import each other. A barrel import leaves a
// constant undefined at runtime.
import { TEMPLATE_STATUS } from 'src/data/template/tags';

import {
  INSIGHT_TEMPLATE_STATE,
  type InsightTemplateState,
  PLAYBOOK_INSIGHT_KIND,
  PLAYBOOK_TEMPLATE_RUN_STATUS,
  type PlaybookTemplateRunStatus,
} from './const';
import type { Playbook, PlaybookInsight, PlaybookTemplateIdea, PlaybookTemplateRun } from './types';

export const isPlaybookTemplateRunGeneratingIdeas = (status?: PlaybookTemplateRunStatus): boolean =>
  status === PLAYBOOK_TEMPLATE_RUN_STATUS.generatingIdeas;

export const isPlaybookTemplateRunReady = (status?: PlaybookTemplateRunStatus): boolean =>
  status === PLAYBOOK_TEMPLATE_RUN_STATUS.completed;

// The run the Build action reopens rather than replacing with a fresh one. A run still generating
// is always reopened — it is the fresh build. A completed one is reopened to show what it produced,
// unless insights were approved since it ran: its templates then rest on a set the playbook no
// longer has, so the click is asking for templates built on the newer ones.
export const getResumablePlaybookTemplateRun = (
  playbook: Playbook
): PlaybookTemplateRun | null | undefined => {
  const run = playbook.templateRun;

  if (isPlaybookTemplateRunGeneratingIdeas(run?.status)) {
    return run;
  }

  return isPlaybookTemplateRunReady(run?.status) && !playbook.templatesOutdated ? run : null;
};

// A unique template pairs one pattern with one directional insight. The directional insight is its
// performance driver. An idea from an older run can cite another combination. That idea has no
// performance driver to name.
export const getPlaybookTemplateIdeaPerformanceDriver = (
  idea: PlaybookTemplateIdea
): string | null => {
  const patterns = idea.sourceInsights.filter(
    ({ insightType }) => insightType === PLAYBOOK_INSIGHT_KIND.pattern
  );
  const directionals = idea.sourceInsights.filter(
    ({ insightType }) => insightType === PLAYBOOK_INSIGHT_KIND.directional
  );

  if (patterns.length !== 1 || directionals.length !== 1) {
    return null;
  }

  return directionals[0].name;
};

// Earlier runs' saved ideas are fetched with the run's own, so they must not hide that this run
// found none.
export const hasPlaybookTemplateRunIdeas = (
  run: Pick<PlaybookTemplateRun, 'id'>,
  ideas: PlaybookTemplateIdea[]
): boolean => ideas.some((idea) => idea.playbookTemplateRunId === run.id);

type SavedTemplate = Pick<
  NonNullable<PlaybookTemplateIdea['customerMultiPromptTemplate']>,
  'status'
>;

// A saved template stays to review until it is published.
const getSavedTemplateState = (template: SavedTemplate): InsightTemplateState =>
  template.status === TEMPLATE_STATUS.draft
    ? INSIGHT_TEMPLATE_STATE.review
    : INSIGHT_TEMPLATE_STATE.use;

// An insight opens only the template built from it, never a playbook-wide idea that cites it.
// A built draft that is not saved yet is a template to review. A build that ended without a
// template fails silently, so the insight offers to build again.
export const getInsightTemplateState = (insight: PlaybookInsight): InsightTemplateState => {
  if (insight.originTemplateIdea) {
    const template = insight.originTemplateIdea.customerMultiPromptTemplate;
    return template ? getSavedTemplateState(template) : INSIGHT_TEMPLATE_STATE.review;
  }

  return isPlaybookTemplateRunGeneratingIdeas(insight.originTemplateRun?.status)
    ? INSIGHT_TEMPLATE_STATE.building
    : INSIGHT_TEMPLATE_STATE.build;
};

// A playbook-wide idea is a candidate until the user saves a template from it. The backend records
// the saved template on the idea.
export const getPlaybookTemplateIdeaState = (idea: PlaybookTemplateIdea): InsightTemplateState =>
  idea.customerMultiPromptTemplate
    ? getSavedTemplateState(idea.customerMultiPromptTemplate)
    : INSIGHT_TEMPLATE_STATE.build;

// A click waits on the server before it opens a template or starts a build. A pending build shows
// the building state before the server confirms a run. If no run starts, the control returns to its
// build state.
export const getInsightTemplateControlState = (
  state: InsightTemplateState,
  pending: boolean
): { shownState: InsightTemplateState; loading: boolean } => {
  const shownState =
    pending && state === INSIGHT_TEMPLATE_STATE.build ? INSIGHT_TEMPLATE_STATE.building : state;

  return { shownState, loading: pending || shownState === INSIGHT_TEMPLATE_STATE.building };
};
